/**
 * TrustGate Security Fixes Implementation
 * 
 * Comprehensive security fixes for all vulnerabilities detected by AI analysis
 * including XSS prevention, SQL injection protection, and CSRF hardening
 */

import React, { useEffect, useState } from 'react';
import { InputSanitizer } from './InputSanitizer';
import { ContentSecurityPolicy } from './ContentSecurityPolicy';

// CSRF Protection Implementation
export class CSRFProtection {
  private static readonly CSRF_TOKEN_KEY = 'csrf_token';
  private static readonly CSRF_HEADER = 'X-CSRF-Token';

  /**
   * Generate and store CSRF token
   */
  static generateToken(): string {
    const token = this.generateRandomToken();
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(this.CSRF_TOKEN_KEY, token);
    }
    return token;
  }

  /**
   * Get current CSRF token
   */
  static getToken(): string | null {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem(this.CSRF_TOKEN_KEY);
    }
    return null;
  }

  /**
   * Validate CSRF token
   */
  static validateToken(requestToken: string): boolean {
    const storedToken = this.getToken();
    return storedToken !== null && storedToken === requestToken;
  }

  /**
   * Add CSRF token to fetch requests
   */
  static addTokenToRequest(request: RequestInit): RequestInit {
    const token = this.getToken();
    if (token) {
      return {
        ...request,
        headers: {
          ...request.headers,
          [this.CSRF_HEADER]: token
        }
      };
    }
    return request;
  }

  /**
   * Generate random token
   */
  private static generateRandomToken(): string {
    const array = new Uint8Array(32);
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      crypto.getRandomValues(array);
    } else {
      // Fallback for environments without crypto
      for (let i = 0; i < array.length; i++) {
        array[i] = Math.floor(Math.random() * 256);
      }
    }
    return btoa(String.fromCharCode(...array));
  }
}

// Safe HTML Renderer Component
export const SafeHTMLRenderer: React.FC<{ content: string; className?: string }> = ({ 
  content, 
  className = '' 
}) => {
  const [sanitizedContent, setSanitizedContent] = useState<string>('');
  const [isValid, setIsValid] = useState<boolean>(true);
  const [warnings, setWarnings] = useState<string[]>([]);

  useEffect(() => {
    const result = InputSanitizer.sanitize(content, { 
      allowHTML: true, 
      allowedTags: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'span', 'ul', 'ol', 'li'],
      allowedAttributes: ['href', 'title', 'target', 'class', 'id']
    });
    
    setSanitizedContent(result.sanitizedValue);
    setIsValid(result.isValid);
    setWarnings(result.warnings);
  }, [content]);

  if (!isValid) {
    return (
      <div className={`text-red-600 ${className}`}>
        <span className="font-semibold">Security Warning:</span> Content contains potentially unsafe elements.
        {warnings.length > 0 && (
          <ul className="text-sm mt-1">
            {warnings.map((warning, index) => (
              <li key={index}>- {warning}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div 
      className={className}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
};

// Safe Input Component
export const SafeInput: React.FC<{
  value: string;
  onChange: (value: string, result: any) => void;
  type?: 'text' | 'email' | 'url' | 'tel';
  placeholder?: string;
  className?: string;
  sanitizeOptions?: any;
}> = ({ 
  value, 
  onChange, 
  type = 'text', 
  placeholder, 
  className = '',
  sanitizeOptions = {}
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [showWarnings, setShowWarnings] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Sanitize based on input type
    let result;
    switch (type) {
      case 'email':
        result = InputSanitizer.sanitizeEmail(newValue);
        break;
      case 'url':
        result = InputSanitizer.sanitizeURL(newValue);
        break;
      case 'tel':
        result = InputSanitizer.sanitizePhone(newValue);
        break;
      default:
        result = InputSanitizer.sanitize(newValue, sanitizeOptions);
    }

    setValidationResult(result);
    onChange(result.sanitizedValue, result);
  };

  const getInputClassName = () => {
    const baseClass = 'border rounded px-3 py-2 w-full';
    const statusClass = validationResult 
      ? validationResult.isValid 
        ? 'border-green-500' 
        : validationResult.threats.length > 0 
          ? 'border-red-500' 
          : 'border-yellow-500'
      : 'border-gray-300';
    return `${baseClass} ${statusClass} ${className}`;
  };

  return (
    <div className="space-y-2">
      <input
        type={type}
        value={inputValue}
        onChange={handleChange}
        placeholder={placeholder}
        className={getInputClassName()}
        onFocus={() => setShowWarnings(true)}
        onBlur={() => setShowWarnings(false)}
      />
      
      {showWarnings && validationResult && !validationResult.isValid && (
        <div className="text-sm text-red-600">
          {validationResult.threats.map((threat: string, index: number) => (
            <div key={index}>- {threat}</div>
          ))}
          {validationResult.warnings.map((warning: string, index: number) => (
            <div key={index} className="text-yellow-600">- {warning}</div>
          ))}
        </div>
      )}
    </div>
  );
};

// Security Hardening Hook
export const useSecurityHardening = () => {
  const [securityStatus, setSecurityStatus] = useState({
    cspActive: false,
    csrfToken: null as string | null,
    violations: [] as any[]
  });

  useEffect(() => {
    // Initialize Content Security Policy
    const cspConfig = process.env.NODE_ENV === 'production' 
      ? ContentSecurityPolicy.createStrictConfig()
      : ContentSecurityPolicy.createDevelopmentConfig();
    
    ContentSecurityPolicy.applyToDocument(cspConfig);
    setSecurityStatus(prev => ({ ...prev, cspActive: true }));

    // Initialize CSRF token
    const token = CSRFProtection.generateToken();
    setSecurityStatus(prev => ({ ...prev, csrfToken: token }));

    // Setup CSP violation monitoring
    ContentSecurityPolicy.setupViolationMonitoring((violation) => {
      setSecurityStatus(prev => ({
        ...prev,
        violations: [...prev.violations, violation]
      }));
    });

    // Override fetch to add CSRF protection
    const originalFetch = window.fetch;
    window.fetch = async (input, init) => {
      const protectedInit = CSRFProtection.addTokenToRequest(init || {});
      return originalFetch(input, protectedInit);
    };

    return () => {
      // Cleanup
      window.fetch = originalFetch;
    };
  }, []);

  const sanitizeInput = (input: string, options?: any) => {
    return InputSanitizer.sanitize(input, options);
  };

  const validateInput = (input: string, type: string) => {
    switch (type) {
      case 'email':
        return InputSanitizer.sanitizeEmail(input);
      case 'url':
        return InputSanitizer.sanitizeURL(input);
      case 'phone':
        return InputSanitizer.sanitizePhone(input);
      case 'json':
        return InputSanitizer.sanitizeJSON(input);
      default:
        return InputSanitizer.sanitize(input);
    }
  };

  return {
    securityStatus,
    sanitizeInput,
    validateInput,
    csrfToken: securityStatus.csrfToken
  };
};

// Security Provider Component
export const SecurityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const security = useSecurityHardening();

  return (
    <div className="security-hardened">
      {children}
      
      {/* Security Status Indicator (Development Only) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 left-4 bg-black/80 text-white p-2 rounded text-xs">
          <div>CSP: {security.securityStatus.cspActive ? 'Active' : 'Inactive'}</div>
          <div>CSRF: {security.securityStatus.csrfToken ? 'Active' : 'Inactive'}</div>
          <div>Violations: {security.securityStatus.violations.length}</div>
        </div>
      )}
    </div>
  );
};

// Database Query Security
export class DatabaseSecurity {
  /**
   * Sanitize database query parameters
   */
  static sanitizeQueryParams(params: any): any {
    if (typeof params !== 'object' || params === null) {
      return params;
    }

    const sanitized: any = {};
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        // Check for prototype pollution
        if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
          continue;
        }

        const value = params[key];
        if (typeof value === 'string') {
          // Remove SQL injection patterns
          sanitized[key] = value
            .replace(/(['";\\])/g, '\\$1')
            .replace(/--/g, '')
            .replace(/\/\*/g, '')
            .replace(/\*\//g, '');
        } else if (typeof value === 'object') {
          sanitized[key] = this.sanitizeQueryParams(value);
        } else {
          sanitized[key] = value;
        }
      }
    }

    return sanitized;
  }

  /**
   * Validate query string
   */
  static validateQueryString(query: string): { isValid: boolean; sanitized: string; threats: string[] } {
    const threats: string[] = [];
    let sanitized = query;

    // Check for SQL injection patterns
    const sqlPatterns = [
      /('|(\\'))|(\-\-)|(\;)|(\|)|(\*)/g,
      /\b(UNION|SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC)\b/gi,
      /\b(OR|AND)\s+\d+\s*=\s*\d+/gi,
      /\/\*.*?\*\//gi
    ];

    sqlPatterns.forEach(pattern => {
      const matches = query.match(pattern);
      if (matches) {
        threats.push(`SQL Injection Pattern: ${matches.join(', ')}`);
        sanitized = sanitized.replace(pattern, '');
      }
    });

    return {
      isValid: threats.length === 0,
      sanitized: sanitized.trim(),
      threats
    };
  }
}

// Export all security components
export default {
  CSRFProtection,
  SafeHTMLRenderer,
  SafeInput,
  useSecurityHardening,
  SecurityProvider,
  DatabaseSecurity,
  InputSanitizer,
  ContentSecurityPolicy
};
